import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaSignInAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import styles from './SignIn.module.css';

const SignIn = () => {
    const navigate = useNavigate();
    const {isAuthenticated, loading, checkAuthentication} = useAuth(); 
    const [correo, setCorreo] = useState('');
    const [contrasenia, setContrasenia] = useState('');
    const [errores, setErrores] = useState({
        correo: '',
        contrasenia: '',
        general: '',
    });
    const [mostrarContrasenia, setMostrarContrasenia] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!loading && isAuthenticated) { 
            navigate('/'); 
        }
    }, [isAuthenticated, loading, navigate]); 

    const validarEmail = (email) => {
        if (!email) return '';

        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        if (!emailRegex.test(email)) {
            return "El correo electrónico no es válido";
        }
        return '';
    };

    const handleChangeCorreo = (e) => {
        const valor = e.target.value;
        setCorreo(valor);
        setErrores(prev => ({
            ...prev,
            correo: validarEmail(valor)
        }));
    };

    const handleChangeContrasenia = (e) => {
        const valor = e.target.value;
        setContrasenia(valor);
        if (errores.contrasenia) {
            setErrores(prev => ({
                ...prev,
                contrasenia: ''
            }));
        }
    };

    const toggleMostrarContrasenia = () => {
        setMostrarContrasenia(!mostrarContrasenia);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Limpiar errores previos
        setErrores({
            correo: '',
            contrasenia: '',
            general: ''
        });

        const nuevosErrores = {
            correo: correo ? validarEmail(correo) : 'Ingresa tu correo.',
            contrasenia: contrasenia ? '' : 'Ingresa tu contraseña.',
            general: ''
        };

        if (nuevosErrores.correo || nuevosErrores.contrasenia) {
            setErrores(nuevosErrores);
            setIsLoading(false);
            return;
        }

        fetch(`/api/auth/login`, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
    },
    body: JSON.stringify({ correo, contrasenia }),
})
        .then(async response => {
            // Intentar obtener el JSON de la respuesta
            let data;
            const contentType = response.headers.get('content-type');
            
            try {
                if (contentType && contentType.includes('application/json')) {
                    data = await response.json();
                } else {
                    // Si no es JSON, obtener como texto
                    const textData = await response.text();
                    data = { error: textData };
                }
            } catch (parseError) {
                // Si falla el parseo, crear un objeto de error genérico
                data = { error: 'Error del servidor' };
            }
            
            if (!response.ok) {
                // Manejar diferentes códigos de estado
                if (response.status === 429) {
                    // Rate limit exceeded
                    throw new Error(data.error || 'Demasiados intentos. Intenta más tarde.');
                } else if (response.status >= 500) {
                    throw new Error('Problema interno del servidor. Intenta más tarde.');
                } else if (response.status === 404) {
                    throw new Error('Servicio no encontrado. Contacta al administrador.');
                } else {
                    throw new Error(data.error || 'Error en la autenticación');
                }
            }
            
            return data;
        })
        .then(data => {
            if(data.token && data.usuario) {
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('user', data.usuario.id.toString());
                localStorage.setItem('userType', data.usuario.tipo.toString());

                // Actualizamos contexto después de un buen login
                checkAuthentication().then(() => { 
                        navigate('/');
                }); 
            }
            setIsLoading(false); 
        })
        .catch(error => { 
            console.error('Error:', error); 
            
            let mensajeError;
            
            // Manejar diferentes tipos de errores
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                mensajeError = 'No se pudo conectar al servidor. Verifica tu conexión a internet.';
            } else if (error.message === 'Failed to fetch') {
                mensajeError = 'Problema de conexión con el servidor. Intenta más tarde.';
            } else if (error.message.includes('NetworkError')) {
                mensajeError = 'Error de red. Verifica tu conexión a internet.';
            } else {
                mensajeError = error.message || 'Error desconocido. Por favor intenta de nuevo.';
            }
            
            setErrores(prev => ({
                ...prev, 
                general: mensajeError
            }));
            setIsLoading(false);
        });   
    };
    
    if (loading) { 
        return <div>Cargando...</div>
    }

    return (
        <div className={styles.loginCuerpo}>
            <div className={styles.loginCuadro}>
                <h1>Iniciar sesión</h1>

                {errores.general && <p className={styles.mensajeError}>{errores.general}</p>}

                <form onSubmit={handleSubmit} className={styles.loginForm}>
                    <div className={styles.campoLogin}>
                        <label htmlFor="correo">
                            <FaUser className={styles.iconoInput} />
                            Correo electrónico:
                        </label>
                        <input
                            type="email"
                            id="correo"
                            className={styles.inputLogin}
                            value={correo}
                            onChange={handleChangeCorreo}
                            required
                            disabled={isLoading}
                        />
                        {errores.correo && <p className={styles.mensajeError}>{errores.correo}</p>}
                    </div>

                    <div className={styles.campoLogin}>
                        <label htmlFor="contrasenia">
                            <FaLock className={styles.iconoInput} />
                            Contraseña:
                        </label>
                        <div className={styles.contenedorPassword}>
                            <input
                                type={mostrarContrasenia ? "text" : "password"}
                                id="contrasenia"
                                className={styles.inputLogin}
                                value={contrasenia}
                                onChange={handleChangeContrasenia}
                                required
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                className={styles.botonMostrar}
                                onClick={toggleMostrarContrasenia}
                                aria-label={mostrarContrasenia ? "Ocultar contraseña" : "Mostrar contraseña"}
                                disabled={isLoading}
                            >
                                {mostrarContrasenia ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        {errores.contrasenia && <p className={styles.mensajeError}>{errores.contrasenia}</p>}
                    </div>

                    <div className={styles.opcionesLogin}>
                        <a 
                            href="#" 
                            onClick={(e) => { e.preventDefault(); navigate('/recuperar-contrasena'); }}
                            className={isLoading ? styles.disabledLink : ''}
                        >
                            ¿Olvidaste tu contraseña?
                        </a>
                    </div>

                    <button type="submit" className={styles.botonLogin} disabled={isLoading}>
                        <FaSignInAlt /> {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                    </button>
                </form>

                <div className={styles.registroOpcion}>
                    <p>¿No tienes una cuenta? <a href="#" onClick={(e) => { e.preventDefault(); navigate('/register'); }} className={isLoading ? styles.disabledLink : ''}>Regístrate aquí</a></p>
                </div>
            </div>
        </div>
    );
}

export default SignIn;